import Check from "@mui/icons-material/Check";
import { Box, Chip, Popover, Stack, useTheme } from "@mui/material";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { VirtualEventStatus } from "generated-graphql/graphql";
import { useCallback, useMemo } from "react";
import { Control, Controller, useController } from "react-hook-form";
import {
  virtualEventStatusToChipSx,
  virtualEventStatusToIcon,
} from "./statusStyles";
import { TaskFormType } from "./types";
import { virtualEventStatusToLabel } from "./util.labels";

type StatusPickerProps = {
  control: Control<TaskFormType>;
  onOpenStatusPopover: (e: React.MouseEvent<HTMLElement>) => void;
  statusPopoverAnchorEl: HTMLElement | null;
  setStatusPopoverAnchorEl: (anchorEl: HTMLElement | null) => void;
  existingTask: TaskFormType;
};
export const StatusPicker = ({
  control,
  onOpenStatusPopover,
  statusPopoverAnchorEl,
  setStatusPopoverAnchorEl,
  existingTask,
}: StatusPickerProps) => {
  const theme = useTheme();

  const {
    field: { onChange: onChangeIsCompleted },
  } = useController({
    control,
    name: "isCompleted",
  });

  const allowedStatusOptions = useMemo(() => {
    switch (existingTask.status) {
      case VirtualEventStatus.Active:
        return [VirtualEventStatus.Active, VirtualEventStatus.Completed];
      case VirtualEventStatus.Overdue:
        return [VirtualEventStatus.Overdue, VirtualEventStatus.Completed];
      case VirtualEventStatus.Completed:
        if (
          existingTask.deadline &&
          new Date(existingTask.deadline) < new Date()
        ) {
          return [VirtualEventStatus.Completed, VirtualEventStatus.Overdue];
        }
        return [VirtualEventStatus.Completed, VirtualEventStatus.Active];
      case null:
      default:
        return [];
    }
  }, [existingTask]);

  const handleStatusChange = useCallback(
    (
      s: VirtualEventStatus,
      status: VirtualEventStatus,
      onChange: () => void
    ) => {
      if (s !== status) {
        // Handle moving into/out of Completed
        const movingIntoCompleted =
          s === VirtualEventStatus.Completed &&
          status !== VirtualEventStatus.Completed;
        const movingOutOfCompleted =
          status === VirtualEventStatus.Completed &&
          s !== VirtualEventStatus.Completed;

        if (movingIntoCompleted) {
          onChangeIsCompleted(true);
        } else if (movingOutOfCompleted) {
          onChangeIsCompleted(false);
        }
        onChange();
      }
      setStatusPopoverAnchorEl(null);
    },
    [onChangeIsCompleted, setStatusPopoverAnchorEl]
  );

  return (
    <Controller
      name="status"
      control={control}
      render={({ field }) => {
        if (!field.value) return <></>;
        const status = field.value;
        const stylingBlock = virtualEventStatusToChipSx(status, theme);
        return (
          <>
            <Chip
              icon={virtualEventStatusToIcon(status)}
              label={virtualEventStatusToLabel(status)}
              sx={{
                ...stylingBlock,
                "& .MuiChip-icon": { fontSize: 14 },
                cursor: "pointer",
              }}
              deleteIcon={<ArrowDropDownIcon />}
              onDelete={onOpenStatusPopover}
              slotProps={{
                root: { onClick: onOpenStatusPopover },
              }}
              size="small"
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
            <Popover
              open={Boolean(statusPopoverAnchorEl)}
              anchorEl={statusPopoverAnchorEl}
              onClose={() => setStatusPopoverAnchorEl(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              sx={{
                "& .MuiPaper-root": { p: 1, paddingLeft: 0 },
              }}
            >
              <Stack spacing={1} sx={{ p: 1 }}>
                {allowedStatusOptions.map((s) => {
                  const chipSx = {
                    ...virtualEventStatusToChipSx(s, theme),
                    "& .MuiChip-icon": { fontSize: 16 },
                  };
                  const isCurrentStatus = s === status;
                  return (
                    <Stack
                      key={s}
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        handleStatusChange(s, status, () => field.onChange(s))
                      }
                    >
                      {isCurrentStatus ? (
                        <Check sx={{ fontSize: 16 }} />
                      ) : (
                        <Box sx={{ width: 16 }} />
                      )}

                      <Chip
                        icon={virtualEventStatusToIcon(s)}
                        label={virtualEventStatusToLabel(s)}
                        sx={chipSx}
                        size="small"
                      />
                    </Stack>
                  );
                })}
              </Stack>
            </Popover>
          </>
        );
      }}
    />
  );
};
